import { groupBy } from '@toggle/helpers';
import { SvgIconNames } from '@toggle/design-system';
import {
  ExploreInsightCondition,
  ExploreInsightDirection,
  IconDisplayRule,
  InsightGroup,
} from '@toggle/toggle';

import {
  FOUNDATIONAL_ICONS,
  MACRO_ICONS,
  TECHNICAL_ICONS,
} from '../../utils/const';

export enum InsightIconVariants {
  SingleGreen = 'single-green',
  SingleRed = 'single-red',
  SingleGrey = 'single-grey',
  ComboVerticalGreen = 'combo-vertical-green',
  ComboVerticalRed = 'combo-vertical-red',
  ComboVerticalGrey = 'combo-vertical-grey',
  ComboHorizontalGreen = 'combo-horizon-green',
  ComboHorizontalRed = 'combo-horizon-red',
  ComboHorizontalGrey = 'combo-horizon-grey',
}

export const singleIconVariants = [
  InsightIconVariants.SingleGreen,
  InsightIconVariants.SingleRed,
  InsightIconVariants.SingleGrey,
];

export const verticalComboIconVariants = [
  InsightIconVariants.ComboVerticalGreen,
  InsightIconVariants.ComboVerticalRed,
  InsightIconVariants.ComboVerticalGrey,
];

const singleInsightBorders = {
  positive: 'SingleInsightBorderPositive' as SvgIconNames,
  negative: 'SingleInsightBorderNegative' as SvgIconNames,
};

const comboVerticalBorders = {
  positive: 'ComboInsightsVerticalBorderPositive' as SvgIconNames,
  negative: 'ComboInsightsVerticalBorderNegative' as SvgIconNames,
};

const comboHorizontalBorders = {
  positive: 'ComboInsightsHorizontalBorderPositive' as SvgIconNames,
  negative: 'ComboInsightsHorizontalBorderNegative' as SvgIconNames,
};

export const variantToBorderMap: Record<
  InsightIconVariants,
  { positive: SvgIconNames; negative: SvgIconNames }
> = {
  [InsightIconVariants.SingleGreen]: singleInsightBorders,
  [InsightIconVariants.SingleRed]: singleInsightBorders,
  [InsightIconVariants.SingleGrey]: singleInsightBorders,
  [InsightIconVariants.ComboVerticalGreen]: comboVerticalBorders,
  [InsightIconVariants.ComboVerticalRed]: comboVerticalBorders,
  [InsightIconVariants.ComboVerticalGrey]: comboVerticalBorders,
  [InsightIconVariants.ComboHorizontalGreen]: comboHorizontalBorders,
  [InsightIconVariants.ComboHorizontalRed]: comboHorizontalBorders,
  [InsightIconVariants.ComboHorizontalGrey]: comboHorizontalBorders,
};

const greenSvgColors = {
  stroke: 'var(--viz-bullish)',
  fill: 'var(--viz-bullish-soft)',
};

const redSvgColors = {
  stroke: 'var(--viz-bearish)',
  fill: 'var(--viz-bearish-soft)',
};

const greySvgColors = {
  stroke: 'var(--icon-disabled)',
  fill: 'var(--surface-default)',
};

export const variantToColorMap: {
  [key in InsightIconVariants]: { stroke: string; fill: string };
} = {
  [InsightIconVariants.SingleGreen]: greenSvgColors,
  [InsightIconVariants.SingleRed]: redSvgColors,
  [InsightIconVariants.SingleGrey]: greySvgColors,
  [InsightIconVariants.ComboVerticalGreen]: greenSvgColors,
  [InsightIconVariants.ComboVerticalRed]: redSvgColors,
  [InsightIconVariants.ComboVerticalGrey]: greySvgColors,
  [InsightIconVariants.ComboHorizontalGreen]: greenSvgColors,
  [InsightIconVariants.ComboHorizontalRed]: redSvgColors,
  [InsightIconVariants.ComboHorizontalGrey]: greySvgColors,
};

const unmatchedMap = {
  vertical: InsightIconVariants.ComboVerticalGrey,
  horizontal: InsightIconVariants.ComboHorizontalGrey,
};
const bearishMap = {
  vertical: InsightIconVariants.ComboVerticalRed,
  horizontal: InsightIconVariants.ComboHorizontalRed,
};
const bullishMap = {
  vertical: InsightIconVariants.ComboVerticalGreen,
  horizontal: InsightIconVariants.ComboHorizontalGreen,
};

export const getSizingValues = ({
  width,
  height,
  iconSize,
  iconsCount,
  comboIconPadding = 0,
}: {
  width: number;
  height: number;
  iconSize: number;
  iconsCount: number;
  comboIconPadding?: number;
}) => {
  const centerX = width / 2;
  const centerY = height / 2;
  const totalComboIconSize =
    iconsCount * iconSize + (iconsCount - 1) * comboIconPadding;

  return {
    svgProps: {
      width,
      height,
      viewBox: `0 0 ${width} ${height}`,
    },
    xOffset: centerX - iconSize / 2,
    yOffset: centerY - iconSize / 2,
    comboStartX: centerX - totalComboIconSize / 2,
    comboStartY: centerY - totalComboIconSize / 2,
  };
};

interface GetIconNameArgs {
  group: InsightGroup;
  category: string;
  driver: string;
}

export const getIconName = ({ group, category, driver }: GetIconNameArgs) => {
  let name = '';
  const iconKey = `${category}.${driver}`;

  switch (group) {
    case InsightGroup.Fundamentals:
      name = FOUNDATIONAL_ICONS[iconKey];
      break;
    case InsightGroup.Technicals:
      name = TECHNICAL_ICONS[iconKey];
      break;
    case InsightGroup.Macroeconomics:
      name = MACRO_ICONS[iconKey];
      break;
  }

  return name;
};

interface GetInsightIconPropsArgs {
  conditions: ExploreInsightCondition[];
  matched: boolean;
  layout?: 'vertical' | 'horizontal';
  direction: ExploreInsightDirection;
}

export const getInsightIconColorAndDirection = ({
  conditions,
  matched,
  direction,
  layout,
}: GetInsightIconPropsArgs): {
  names: SvgIconNames[];
  style: InsightIconVariants;
  iconDirection: 'positive' | 'negative';
} => {
  const names = conditions.map(({ category, group, driver }) =>
    getIconName({ category, group, driver })
  ) as SvgIconNames[];
  let style = InsightIconVariants.SingleRed;

  const isBearish = direction === ExploreInsightDirection.Bearish;

  if (!matched) {
    style = layout ? unmatchedMap[layout] : InsightIconVariants.SingleGrey;
  }

  if (matched && isBearish) {
    style = layout ? bearishMap[layout] : InsightIconVariants.SingleRed;
  }

  if (matched && !isBearish) {
    style = layout ? bullishMap[layout] : InsightIconVariants.SingleGreen;
  }

  const iconDirection = isBearish ? 'negative' : 'positive';

  return {
    names,
    style,
    iconDirection,
  };
};

export const getConditionIcons = ({
  kind,
  conditions,
}: {
  kind?: IconDisplayRule;
  conditions?: ExploreInsightCondition[];
}) => {
  if (!conditions?.length) {
    return [];
  }

  if (kind === 'combination') {
    const grouped = groupBy(conditions, c => String(c.group));
    return Object.values(grouped)
      .slice(0, 2)
      .map(bucket => bucket[0]);
  }

  return [conditions[0]];
};
